/**
 * USE_AUDIO_ANALYSER.JS - Hook pour analyser l'audio en temps réel
 * 
 * Ce hook se connecte au contexte audio de Howler.js et fournit
 * des fonctions pour extraire les données de fréquence.
 * 
 * FONCTIONNALITÉS:
 * - Analyse FFT (Fast Fourier Transform)
 * - Extraction par plage de fréquences
 * - Données normalisées (0-1)
 * - Lissage configurable
 * 
 * UTILISATION:
 * ```javascript
 * const { getFrequencyRange } = useAudioAnalyser()
 * 
 * useFrame(() => {
 *   const bass = getFrequencyRange(0, 0.2)  // Basses (0-20%)
 *   // Utiliser la valeur pour animer...
 * })
 * ```
 */

import { useEffect, useState, useRef } from 'react'
import { Howler } from 'howler'

export function useAudioAnalyser() {
  const [analyser, setAnalyser] = useState(null)
  const dataArrayRef = useRef(null)

  useEffect(() => {
    // ============================================
    // 1. RÉCUPÉRER LE CONTEXTE AUDIO DE HOWLER
    // ============================================
    
    // Howler.ctx est le contexte Web Audio API partagé
    const ctx = Howler.ctx

    if (!ctx) {
      console.warn('⚠️ Contexte audio non disponible')
      return
    }

    // ============================================
    // 2. CRÉER L'ANALYSERNODE
    // ============================================
    
    // AnalyserNode : noeud Web Audio API pour analyse FFT
    const analyserNode = ctx.createAnalyser()
    
    // FFT Size : Taille de la fenêtre FFT (doit être puissance de 2)
    // 256 = bon équilibre entre précision et performance
    analyserNode.fftSize = 256
    
    // Smoothing : Lissage temporel (0-1)
    // 0.8 = bon équilibre, smooth mais réactif
    analyserNode.smoothingTimeConstant = 0.8

    // ============================================
    // 3. CONNECTER L'ANALYSER DANS LA CHAÎNE AUDIO
    // ============================================
    
    // Chaîne audio : sons → masterGain → analyser → destination (speakers)
    const masterGain = Howler.masterGain
    
    if (masterGain) {
      // Déconnecter le gain de la destination
      masterGain.disconnect()
      
      // Reconnecter via l'analyser (pour intercepter le signal)
      masterGain.connect(analyserNode)
      analyserNode.connect(ctx.destination)
      
    }

    // ============================================
    // 4. CRÉER LE BUFFER POUR LES DONNÉES
    // ============================================
    
    // frequencyBinCount : Nombre de bins de fréquence (fftSize / 2)
    const bufferLength = analyserNode.frequencyBinCount
    
    // Uint8Array : Tableau de valeurs 0-255 pour stocker les données FFT
    dataArrayRef.current = new Uint8Array(bufferLength)

    setAnalyser(analyserNode)


    // ============================================
    // 5. CLEANUP AU DÉMONTAGE
    // ============================================
    
    return () => {
      
      if (masterGain) {
        // Déconnecter l'analyser
        analyserNode.disconnect()
        masterGain.disconnect()
        
        // Reconnecter directement le gain à la destination
        masterGain.connect(ctx.destination)
      }
    }
  }, [])

  /**
   * Récupère les données de fréquence normalisées (0-1)
   * 
   * @param {string} type - 'frequency' (spectre) ou 'waveform' (forme d'onde)
   * @returns {Float32Array|null} Données normalisées ou null si indisponible
   * 
   * TYPES:
   * - 'frequency' : Spectre de fréquences (défaut, pour visualisations)
   * - 'waveform' : Forme d'onde dans le temps (pour oscilloscopes)
   */
  const getFrequencyData = (type = 'frequency') => {
    if (!analyser || !dataArrayRef.current) return null

    // Remplir le buffer avec les données audio actuelles
    if (type === 'frequency') {
      // getByteFrequencyData : Données FFT (spectre de fréquences)
      analyser.getByteFrequencyData(dataArrayRef.current)
    } else {
      // getByteTimeDomainData : Forme d'onde dans le temps
      analyser.getByteTimeDomainData(dataArrayRef.current)
    }

    // Normaliser les valeurs de 0-255 à 0-1
    return dataArrayRef.current.map(v => v / 255)
  }

  /**
   * Récupère une valeur moyenne pour une plage de fréquences
   * 
   * @param {number} lowFreq - Fréquence basse (0-1)
   * @param {number} highFreq - Fréquence haute (0-1)
   * @returns {number} Valeur moyenne (0-1)
   * 
   * EXEMPLES DE PLAGES:
   * - Basses:    getFrequencyRange(0, 0.2)      // 0-20% (kick, bass)
   * - Sub-bass:  getFrequencyRange(0, 0.05)     // 0-5% (très profond)
   * - Médiums:   getFrequencyRange(0.2, 0.6)    // 20-60% (voix, guitares)
   * - Aigus:     getFrequencyRange(0.6, 1.0)    // 60-100% (cymbales, hi-hats)
   * - Tout:      getFrequencyRange(0, 1)        // Volume général
   */
  const getFrequencyRange = (lowFreq = 0, highFreq = 1) => {
    if (!analyser || !dataArrayRef.current) return 0

    // Récupérer les données de fréquence
    analyser.getByteFrequencyData(dataArrayRef.current)

    const data = dataArrayRef.current
    
    // Calculer les index de début et fin dans le buffer
    const start = Math.floor(lowFreq * data.length)
    const end = Math.floor(highFreq * data.length)

    // Calculer la somme des valeurs dans la plage
    let sum = 0
    for (let i = start; i < end; i++) {
      sum += data[i]
    }

    // Calculer la moyenne
    const average = sum / (end - start)
    
    // Normaliser (0-255 → 0-1)
    return average / 255
  }

  return {
    analyser,           // L'AnalyserNode (pour accès direct si besoin)
    getFrequencyData,   // Récupérer toutes les données de fréquence
    getFrequencyRange   // Récupérer une moyenne pour une plage spécifique
  }
}

